import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import {
    Box,
    Button,
    Dialog,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Search } from "lucide-react";
import { useForm } from "react-hook-form";
import CustomAutocompleteField from "../../common/CustomAutocompleteField";
import { fetchAllWoundCodesByQuery, fetchWoundInfoByWoundCodes } from "../../../redux/slice/ivr/woundCodeSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useModalStyles } from "../../common/useModalStyles";
import React from "react";

interface IVRWoundSearchPopupProps {
    open: boolean;
    onClose: () => void;
    onSubmitData: (data: any) => void;
}

const IVRWoundSearchPopup: React.FC<IVRWoundSearchPopupProps> = ({
    open,
    onClose,
    onSubmitData,
}) => {
    const { control, handleSubmit, getValues } = useForm();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        bodySx,
        footerSx,
        closeIconSx,
        inputFieldWithLabelSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();
    const dispatch = useAppDispatch();

    const onSubmit = async () => {
        onSubmitData(getValues("icdCode"));
        onClose();
    };

    const fetchWoundCodesByWoundKey = async (query: string, type?: string) => {
        const isValid = /^[a-zA-Z0-9\- ()\[\],.]*$/.test(query);

        if (!isValid) {
            return [];
        }

        if (query.length < 2 && type === "ICD10") {
            return [];
        }
        try {
            const woundCodeType = type ?? "";
            const response = await dispatch(
                fetchAllWoundCodesByQuery({ searchQuery: query, woundType: woundCodeType, skipLoader: true })
            ).unwrap();
            return response;
        } catch (err) {
            console.error("Failed to fetch wound codes:", err);
            return [];
        }
    };

    const fetchWoundsByWoundCodes = async (code: string, type?: string) => {
        try {
            const woundCodeType = type ?? "";
            const response = await dispatch(
                fetchWoundInfoByWoundCodes({ key: code, woundType: woundCodeType })
            ).unwrap();
            return response;
        } catch (err) {
            console.error("Failed to fetch wound codes:", err);
            return [];
        }
    };

    const transformWoundCodeResponse = (res: any, woundCodeType?: string): any[] => {
        if (woundCodeType === "ICD10") {
            return (res?.data?.ICD10 || []).map((item: any) => ({
                value: item.woundKey,
                label: `${item.woundKey} - ${item.woundDisplayValue}`,
            }));
        }
        return [];
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: paperSx(isMobile) }}
            aria-labelledby="ivr-wound-search-dialog-title"
            onClick={(event) => event.stopPropagation()}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={headerSx}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        <Search size={22} color={tokens.accent} strokeWidth={1.8} />
                    </Box>
                    <Box sx={{ minWidth: 0, pr: 4 }}>
                        <Typography
                            id="ivr-wound-search-dialog-title"
                            fontWeight={700}
                            sx={{
                                fontSize: "1.1rem",
                                color: tokens.textPrimary,
                                lineHeight: 1.3,
                            }}
                        >
                            Search ICD-10 Codes
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                            Find and insert wound diagnosis codes
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>

                <Box sx={bodySx}>
                    <Box sx={inputFieldWithLabelSx()}>
                        <CustomAutocompleteField
                            name="icdCode"
                            control={control}
                            label="ICD-10 Code"
                            size="small"
                            fetchFunction={fetchWoundCodesByWoundKey}
                            initialFetchFunction={fetchWoundsByWoundCodes}
                            woundCodeType="ICD10"
                            transformResponse={transformWoundCodeResponse}
                            initialCodes={getValues("icdCode")}
                            isMultiple={true}
                        />
                    </Box>
                </Box>

                <Box sx={footerSx}>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            startIcon={<SendIcon sx={{ fontSize: "1rem !important" }} />}
                            sx={primaryBtnSx}
                        >
                            Insert
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            type="button"
                            onClick={onClose}
                            startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                            sx={cancelBtnSx}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </form>
        </Dialog>
    );
};

export default IVRWoundSearchPopup;
